import { PhoneInput, Seo, Spinner } from '@frontend/shared/web';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { FormField } from '../../components/common/FormField';
import { PublicShell } from '../../components/layout/PublicShell';
import { passwordSchema } from '../../domain/password';
import { getHttpStatus, toUserErrorMessage } from '../../lib/apiError';
import { useAcceptInvitation } from '../../services/api/hooks/useAcceptInvitation';
import { useExtranetLogin } from '../../services/api/hooks/useExtranetLogin';
import { useInvitationPreview } from '../../services/api/hooks/useInvitationPreview';
import type { InvitationPreviewOutput } from '../../types/extranet-api.types';

const schema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(values => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Les mots de passe ne correspondent pas',
  });

type AcceptFormValues = z.infer<typeof schema>;

/**
 * Page publique d'acceptation d'invitation, ouverte depuis le lien reçu par
 * email :
 *
 *     <host>/invitation/accept?token=<token>&accountKey=<accountKey>
 *
 * **Ce format est le contrat avec le back** : Yohan génère ce lien dans l'email
 * d'invitation (acté le 3 septembre 2026). Le changer casse tous les liens déjà
 * envoyés — passer par lui d'abord.
 *
 * Le bucket GCS renvoie `index.html` sur toutes les routes, donc ce lien profond
 * fonctionne en accès direct (vérifié sur extranet-dev).
 *
 * La route vit hors des deux layouts : un utilisateur déjà connecté doit pouvoir
 * l'ouvrir sans être renvoyé sur `/`.
 */
export const AcceptInvitationRoute = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const accountKey = searchParams.get('accountKey');

  const preview = useInvitationPreview(token, accountKey);

  if (!token || !accountKey) {
    return (
      <InvalidLink description="Ce lien d'invitation est incomplet. Utilisez le lien reçu par email ou demandez une nouvelle invitation à votre administrateur." />
    );
  }

  if (preview.isPending) {
    return (
      <>
        <Seo title="Invitation — Portail extranet" />
        <Spinner />
      </>
    );
  }

  if (preview.isError) {
    if (getHttpStatus(preview.error) === 404) {
      return (
        <InvalidLink description="Ce lien d'invitation n'est plus valide. Demandez une nouvelle invitation à votre administrateur." />
      );
    }
    const { description } = toUserErrorMessage(preview.error, "Impossible de charger l'invitation");
    return (
      <PublicShell title="Invitation" subtitle={description}>
        <button
          type="button"
          onClick={() => void preview.refetch()}
          className="h-10 w-full rounded-md bg-zinc-900 px-4 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
        >
          Réessayer
        </button>
      </PublicShell>
    );
  }

  return <AcceptForm token={token} accountKey={accountKey} preview={preview.data} />;
};

const InvalidLink = ({ description }: { description: string }) => (
  <>
    <Seo title="Invitation — Portail extranet" />
    <PublicShell title="Lien d'invitation invalide" subtitle={description}>
      <Link
        to="/login"
        className="h-10 w-full inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
      >
        Aller à la connexion
      </Link>
    </PublicShell>
  </>
);

const hasDialedNumber = (phone: string) => phone.replace(/\D/g, '').length >= 6;

const AcceptForm = ({
  token,
  accountKey,
  preview,
}: {
  token: string;
  accountKey: string;
  preview: InvitationPreviewOutput;
}) => {
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const login = useExtranetLogin();
  const [phone, setPhone] = useState<{ phone: string; country: string } | null>(null);
  const [failure, setFailure] = useState<'accept' | 'login' | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const isPending = accept.isPending || login.isPending;

  const onSubmit: SubmitHandler<AcceptFormValues> = async ({ password }) => {
    setFailure(null);
    const withPhone = phone && hasDialedNumber(phone.phone);
    try {
      await accept.mutateAsync({
        token,
        accountKey,
        password,
        phoneNumber: withPhone ? phone.phone : undefined,
        phoneNumberCountry: withPhone ? phone.country.toUpperCase() : undefined,
      });
    } catch {
      setFailure('accept');
      return;
    }
    try {
      await login.mutateAsync({ email: preview.email, password });
      navigate('/', { replace: true });
    } catch {
      setFailure('login');
    }
  };

  const company = preview.companyName ?? preview.accountKey;

  return (
    <>
      <Seo title="Invitation — Portail extranet" />
      <PublicShell
        title={`Bienvenue ${preview.firstName}`}
        subtitle={`Choisissez votre mot de passe pour rejoindre le portail extranet de ${company}.`}
        footer="Besoin d'aide ? Contactez votre administrateur Allocab."
      >
        <form
          className="flex flex-col gap-3 bg-white border border-zinc-200 rounded-lg p-6 shadow-sm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField id="accept-email" label="Email" value={preview.email} readOnly disabled autoComplete="username" />
          <FormField
            id="accept-password"
            type="password"
            label="Mot de passe"
            autoComplete="new-password"
            hint="8 caractères minimum, une majuscule et un chiffre."
            {...register('password')}
            error={errors.password?.message}
          />
          <FormField
            id="accept-confirm-password"
            type="password"
            label="Confirmer le mot de passe"
            autoComplete="new-password"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />
          <div className="flex flex-col gap-1.5">
            <PhoneInput id="accept-phone" label="Téléphone (optionnel)" onChange={setPhone} disabled={isPending} />
          </div>

          {failure === 'accept' ? (
            <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 mt-1" role="alert">
              {getHttpStatus(accept.error) === 404
                ? "Ce lien d'invitation n'est plus valide."
                : "Impossible d'activer le compte : mot de passe refusé ou lien expiré."}
            </p>
          ) : null}
          {failure === 'login' ? (
            <p
              className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mt-1"
              role="alert"
            >
              Votre compte est activé, mais la connexion automatique a échoué.{' '}
              <Link to="/login" className="underline underline-offset-2">
                Se connecter
              </Link>
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isPending || failure === 'login'}
            className="h-10 w-full rounded-md bg-zinc-900 px-4 text-sm font-medium text-white transition-colors duration-100 hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 mt-2"
          >
            {accept.isPending ? 'Activation…' : login.isPending ? 'Connexion…' : 'Activer mon compte'}
          </button>
        </form>
      </PublicShell>
    </>
  );
};
